import React, { useRef } from 'react';
import { X, Printer } from 'lucide-react';
import { formatWarehouseDate } from '../../../../../utils/dateUtils';

const WarehousePdfModal = ({ documentData, documentType = 'loading', isOpen, onClose, supplierLogoUrl, companyName }) => {
    const printAreaRef = useRef(null);

    if (!isOpen || !documentData) return null;

    const isTransfer = documentType === 'transfer' || !!documentData.transferNo;
    const isOffload = documentType === 'offload' || documentData.requestNo?.startsWith('OR');

    const docTitle = isTransfer
        ? 'TRANSFER SUMMARY'
        : isOffload
        ? 'OFFLOAD REQUEST'
        : 'LOADING REQUEST';

    const docNumber = documentData.transferNo || documentData.requestNo;

    const handlePrint = () => {
        if (!printAreaRef.current) {
            window.print();
            return;
        }

        const content = printAreaRef.current.innerHTML;
        const printWindow = window.open('', '_blank', 'width=900,height=800');

        if (!printWindow) {
            window.print();
            return;
        }

        const headStyles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
            .map(node => node.outerHTML)
            .join('\n');

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>${docTitle} - ${docNumber}</title>
                ${headStyles}
                <style>
                    @page {
                        size: A4 portrait;
                        margin: 10mm;
                    }
                    body {
                        background: #ffffff !important;
                        color: #0f172a !important;
                        margin: 0 !important;
                        padding: 16px !important;
                        overflow: visible !important;
                    }
                    .custom-scrollbar {
                        overflow: visible !important;
                    }
                </style>
            </head>
            <body>
                <div id="printable-pdf-area">
                    ${content}
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 300);
    };

    const logoSrc = supplierLogoUrl || documentData.supplierLogoUrl || '/yaseero-logo.jpeg';
    const displayCompanyName = companyName || documentData.companyName || 'SEHAB ALBARAKAH GOODS WHOLESALERS LLC';
    const formattedDocDate = formatWarehouseDate(documentData.date || documentData.loadingDate || documentData.requestDate);
    const formattedCompletionTime = documentData.completionTime ? formatWarehouseDate(documentData.completionTime, true) : null;
    const formattedPrintedOn = formatWarehouseDate(new Date(), true);
    const staffName = documentData.completedBy || documentData.salesperson || 'Warehouse Staff';

    return (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto print:p-0 print:bg-white print:static print:inset-auto print:backdrop-filter-none print:overflow-visible">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden my-auto print:shadow-none print:border-none print:m-0 print:max-w-none print:max-h-none print:overflow-visible">
                
                {/* Modal Toolbar (Hidden when printing) */}
                <div className="p-3 sm:p-4 bg-slate-800 text-white flex items-center justify-between flex-shrink-0 print:hidden">
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-xs sm:text-sm">{docTitle} - {docNumber}</span>
                        <span className="text-[10px] sm:text-xs text-slate-400 hidden sm:inline">(A4 Printable Document)</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <button
                            onClick={handlePrint}
                            className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                        >
                            <Printer className="w-4 h-4" /> Print PDF
                        </button>
                        <button onClick={onClose} className="p-1 text-slate-400 hover:text-white rounded-lg cursor-pointer">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Printable Document Body */}
                <div ref={printAreaRef} className="p-4 sm:p-8 md:p-12 space-y-6 text-slate-900 bg-white font-sans text-xs flex-1 overflow-y-auto custom-scrollbar print:p-4 print:overflow-visible" id="printable-pdf-area">
                    
                    {/* Document Header */}
                    <div className="flex flex-col sm:flex-row items-start justify-between border-b-2 border-slate-900 pb-6 gap-4">
                        <div className="space-y-1">
                            <div className="flex items-center gap-3">
                                {logoSrc ? (
                                    <img src={logoSrc} alt="Logo" className="w-10 h-10 sm:w-12 sm:h-12 object-contain rounded-xl border border-slate-100" />
                                ) : (
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 text-white font-black text-xl rounded-xl flex items-center justify-center">
                                        {displayCompanyName?.charAt(0) || 'S'}
                                    </div>
                                )}
                                <div>
                                    <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 block leading-tight">{displayCompanyName}</h2>
                                </div>
                            </div>
                            <div className="text-[11px] text-slate-500 pt-1">Dubai, UAE | Tel: +971 50 123 4567</div>
                        </div>

                        <div className="flex items-center sm:items-end justify-between sm:flex-col gap-3 w-full sm:w-auto text-right">
                            <div>
                                <h1 className="text-lg sm:text-xl font-black uppercase text-slate-900 tracking-wider">{docTitle}</h1>
                                <span className="text-xs font-bold text-slate-500 block mt-1">{docNumber}</span>
                            </div>
                        </div>
                    </div>

                    {/* Metadata Grid Box */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 p-3 sm:p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                        <div>
                            <span className="text-slate-500 font-semibold block text-[10px] uppercase">
                                {isTransfer ? 'Transfer No' : 'Request No'}
                            </span>
                            <span className="font-bold text-slate-900 text-xs sm:text-sm">{docNumber}</span>
                        </div>
                        <div>
                            <span className="text-slate-500 font-semibold block text-[10px] uppercase">
                                {isTransfer ? 'Transfer Date' : 'Loading/Request Date'}
                            </span>
                            <span className="font-medium text-slate-800 text-[11px] sm:text-xs">{formattedDocDate}</span>
                        </div>
                        <div>
                            <span className="text-slate-500 font-semibold block text-[10px] uppercase">Vehicle</span>
                            <span className="font-bold text-slate-900">{documentData.vehicle || 'Van 01'}</span>
                        </div>
                        <div>
                            <span className="text-slate-500 font-semibold block text-[10px] uppercase">Salesperson</span>
                            <span className="font-medium text-slate-800">{documentData.salesperson || '—'}</span>
                        </div>
                        <div>
                            <span className="text-slate-500 font-semibold block text-[10px] uppercase">Warehouse</span>
                            <span className="font-medium text-slate-800">{documentData.warehouse || documentData.fromLocation || 'Main Warehouse'}</span>
                        </div>
                        {isTransfer && (
                            <div>
                                <span className="text-slate-500 font-semibold block text-[10px] uppercase">Linked Request</span>
                                <span className="font-bold text-blue-600">{documentData.requestNo}</span>
                            </div>
                        )}
                        {formattedCompletionTime && (
                            <div>
                                <span className="text-slate-500 font-semibold block text-[10px] uppercase">Completion Time</span>
                                <span className="font-medium text-slate-800">{formattedCompletionTime}</span>
                            </div>
                        )}
                    </div>

                    {/* Items Table */}
                    <div className="border border-slate-200 rounded-xl overflow-x-auto">
                        <table className="w-full text-left text-xs min-w-[500px] sm:min-w-0">
                            <thead className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200 uppercase text-[10px]">
                                <tr>
                                    <th className="p-3 pl-4">No.</th>
                                    <th className="p-3">Product Description</th>
                                    <th className="p-3 text-center">Qty</th>
                                    <th className="p-3">Unit</th>
                                    {isOffload ? (
                                        <th className="p-3">Return Reason</th>
                                    ) : (
                                        <th className="p-3">Source (Orders)</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 font-medium">
                                {(documentData.items || []).map((item, idx) => {
                                    const req = item.requestedQty || item.suggestedQty;
                                    const final = item.warehouseQty || item.qty || req;
                                    const isDifferent = req != null && final != null && Number(req) !== Number(final);

                                    return (
                                        <tr key={idx}>
                                            <td className="p-3 pl-4 text-slate-400">{idx + 1}</td>
                                            <td className="p-3 font-semibold text-slate-900">{item.productName}</td>
                                            <td className="p-3 text-center font-bold text-slate-900">
                                                <span>{final}</span>
                                                {isDifferent && (
                                                    <span className="block text-[9px] font-medium text-slate-500">
                                                        (Req: {req})
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-3 text-slate-500">{item.unit || 'Pcs'}</td>
                                            <td className="p-3 text-slate-600">{item.reason || item.source || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer Remarks & Signatures */}
                    <div className="pt-4 space-y-6">
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <span className="font-bold text-slate-700 block mb-0.5 text-[11px]">Warehouse Remarks:</span>
                            <p className="text-slate-600 italic text-xs">{documentData.warehouseRemarks || 'All items prepared, verified, and loaded.'}</p>
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-slate-500 text-[11px] gap-2">
                            <div>Prepared / Completed By: <strong className="text-slate-800">{staffName}</strong></div>
                            <div>Printed On: <strong className="text-slate-800">{formattedPrintedOn}</strong></div>
                        </div>

                        {/* Signature Area */}
                        <div className="pt-8 sm:pt-12 grid grid-cols-2 gap-6 sm:gap-12 text-center text-xs">
                            <div className="space-y-2">
                                <div className="border-b border-slate-400 w-full h-8 sm:h-12"></div>
                                <span className="font-bold text-slate-800 block text-xs">Salesperson Signature</span>
                                <span className="text-[10px] text-slate-400">({documentData.salesperson || 'Salesperson'})</span>
                            </div>
                            <div className="space-y-2">
                                <div className="border-b border-slate-400 w-full h-8 sm:h-12"></div>
                                <span className="font-bold text-slate-800 block text-xs">Warehouse Staff Signature</span>
                                <span className="text-[10px] text-slate-400">({staffName})</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WarehousePdfModal;
